import { registerLocaleData } from '@angular/common';
import localeEnNz from '@angular/common/locales/en-NZ';
import localeEs from '@angular/common/locales/es';
import localeEsUs from '@angular/common/locales/es-US';
import localeSv from '@angular/common/locales/sv';
import dayjs from 'dayjs';
import 'dayjs/locale/en-nz';
import 'dayjs/locale/es';
import 'dayjs/locale/es-us';
import 'dayjs/locale/sv';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { ResolvedLocale, getSupportedLocale } from '@server/utility/locale';

/**
 * Sets the date locale before any other app code runs.
 *
 * This must stay the FIRST import in `main.ts`. Some modules read the dayjs
 * locale while they load (the daterangepicker's default week start and day
 * names), and Angular's `LOCALE_ID` is fixed at bootstrap, so setting the
 * locale after login is too late. Instead the locale resolved on the last
 * visit is cached and applied here; `AppLocaleService` re-resolves it once
 * the user and library are known and reloads if it changed.
 *
 * It is also the one place dayjs plugins are registered.
 */

export const LOCALE_STORAGE_KEY = 'liqLocale';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(isoWeek);

/**
 * Angular's es-US data writes the short date day first (`d/M/y`), but es-US means US date order here
 * (D2): dayjs, the presets and the date pickers show 03/04/2026 for 4 March. So `| date:'short'` and
 * `'shortDate'` use en-US's month-first short date (`M/d/yy`) too; the other formats are unchanged.
 */
function usOrderShortDate(data: unknown[]): unknown[] {
    const DATE_FORMATS = 10; // Angular's LocaleDataIndex.DateFormat: [short, medium, long, full]
    const copy = [...data];
    copy[DATE_FORMATS] = ['M/d/yy', ...(data[DATE_FORMATS] as string[]).slice(1)];
    return copy;
}

/**
 * Stopgap until the currency ticket (D5, D36): amounts are US dollars, and every locale but en-US and
 * es-US names them "US$". Before the app had a locale everyone saw "$1,234.50", so US
 * dollars keep the "$" sign in every locale. Only the sign changes; grouping and decimals still follow
 * the locale (Swedish 1 234,50 $).
 */
function usDollarSign(data: unknown[]): unknown[] {
    const CURRENCIES = 18; // Angular's LocaleDataIndex.Currencies: { code: [symbol, narrow symbol] }
    const copy = [...data];
    copy[CURRENCIES] = { ...(data[CURRENCIES] as Record<string, unknown>), USD: ['$', '$'] };
    return copy;
}

registerLocaleData(usDollarSign(localeEnNz), 'en-NZ');
registerLocaleData(usDollarSign(localeEs), 'es');
registerLocaleData(usDollarSign(usOrderShortDate(localeEsUs)), 'es-US');
registerLocaleData(usDollarSign(localeSv), 'sv');

const readCachedLocale = (): ResolvedLocale => {
    try {
        return getSupportedLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    } catch {
        return getSupportedLocale(null);
    }
};

/** The locale this page load is running with. Fixed until the next reload. */
export const bootLocale: ResolvedLocale = readCachedLocale();

dayjs.locale(bootLocale.dayjs);
